import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import type { PendingSchoolAccount } from '@common/models/account/school/pending-school-account.modl'
import { PendingSchoolAccountService } from '@common/services/pending-school-account-service'
import { useAdminAccount } from './AdminAccountContext'

type PendingSchoolAccountContextType = {
  pendingSchoolAccounts: PendingSchoolAccount[];
  isLoading: boolean;
  error: Error | null;
};

const PendingSchoolAccountContext = createContext<PendingSchoolAccountContextType>({
  pendingSchoolAccounts: [],
  isLoading: true,
  error: null,
})

export const PendingSchoolAccountProvider = ({ children }: { children: ReactNode }) => {
  const [pendingSchoolAccounts, setPendingSchoolAccounts] = useState<PendingSchoolAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const { adminAccount, isLoading: adminAccountLoading } = useAdminAccount()

  useEffect(() => {
    if (adminAccountLoading) return

    if (!adminAccount) {
      setPendingSchoolAccounts([])
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)
    const service = new PendingSchoolAccountService()
    const unsubscribe = service.subscribePendingSchoolAccounts(
      (accounts) => {
        setPendingSchoolAccounts(accounts)
        setIsLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Failed to subscribe pending school accounts:', err)
        setError(err)
        setIsLoading(false)
      },
    )
    return () => unsubscribe()
  }, [adminAccount?.id, adminAccountLoading])

  return (
    <PendingSchoolAccountContext.Provider value={{ pendingSchoolAccounts, isLoading, error }}>
      {children}
    </PendingSchoolAccountContext.Provider>
  );
};

export const usePendingSchoolAccounts = () => {
  const context = useContext(PendingSchoolAccountContext);
  if (!context) {
    throw new Error('usePendingSchoolAccounts must be used within a PendingSchoolAccountProvider');
  }
  return context;
};

